import { useRef, useState } from "react";
import { toast } from "sonner";
import { PenLine, Brush, Puzzle, Check } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { MoodPicker } from "./MoodPicker";
import { DrawPad } from "./DrawPad";
import { GamePicker, GAME_INFO } from "./games/GamePicker";

const PROMPTS = [
  "What did this lesson show you about who you really are?",
  "When you turned inward during the lesson, what did you notice about yourself?",
  "How is this idea already alive inside you, in your body, mind or feelings?",
];
const MODES = [["write", "Write", PenLine], ["draw", "Draw or handwrite", Brush], ["game", "Games", Puzzle]];

export const ReflectStep = ({ req, lesson, data, setData, selfPrompt, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("write");
  const [drew, setDrew] = useState(false);
  const [game, setGame] = useState(null);
  const [gameKind, setGameKind] = useState(null);
  const [gameSeen, setGameSeen] = useState(false);
  const pad = useRef(null);
  const [prompt] = useState(() => selfPrompt || PROMPTS[Math.floor(Math.random() * PROMPTS.length)]);
  const has = { write: !!data.reflection.trim(), draw: drew, game: !!game };

  const save = async () => {
    if (!has.write && !has.draw && !has.game) return toast.error("Write, draw or play the game to finish your reflection.");
    setSaving(true);
    try {
      let drawing_id = null;
      if (has.draw && pad.current) {
        const fd = new FormData();
        fd.append("file", await pad.current.toBlob(), "drawing.png");
        drawing_id = (await http.post("/journal/drawing", fd)).data.id;
      }
      await http.post("/journal", { ...req, ...data, drawing_id, game });
      toast.success("Reflection saved to your journal.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e) || "Could not save. Please try again."); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="reflect-step">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">Discovering myself</p>
        <p className="font-display text-2xl text-[#2C4A3E] mt-2" data-testid="reflection-prompt">{prompt}</p>
        <p className="text-xs text-stone-500 mt-1">Pick any way to reflect. One is enough, or mix them.</p>
        <div className="mt-4 flex flex-wrap gap-1 p-1 rounded-full bg-[#F1EADB] w-fit" role="tablist" data-testid="reflect-modes">
          {MODES.map(([k, label, Icon]) => (
            <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => { setMode(k); if (k === "game") setGameSeen(true); }} data-testid={`reflect-mode-${k}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-colors ${mode === k ? "bg-[#2C4A3E] text-white" : "text-[#2C4A3E] hover:bg-white/70"}`}>
              <Icon className="w-4 h-4" />{label}{has[k] && <Check className="w-3.5 h-3.5" data-testid={`reflect-done-${k}`} />}</button>))}
        </div>
        <div className={mode === "write" ? "" : "hidden"}>
          <textarea rows={5} value={data.reflection} onChange={(e) => setData({ ...data, reflection: e.target.value })} data-testid="reflection-textarea"
            className="mt-4 w-full p-5 rounded-3xl bg-white/70 border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] leading-relaxed" placeholder="Write freely…" />
        </div>
        <div className={`mt-4 ${mode === "draw" ? "" : "hidden"}`}><DrawPad ref={pad} onChange={setDrew} /></div>
        {gameSeen && <div className={`mt-4 rounded-3xl bg-white/60 border border-[#E6DEC3] p-5 ${mode === "game" ? "" : "hidden"}`}>
          {game ? <p className="text-[#2E7D4F] font-medium" data-testid="sort-game-result">{GAME_INFO[game.kind]?.name || "Game"} finished: {game.correct}/{game.total} on the first try{game.seconds != null ? ` in ${game.seconds}s` : ""}. <button type="button" onClick={() => setGame(null)} className="underline text-[#2C4A3E] ml-2" data-testid="sort-game-replay">Play again</button></p>
            : <GamePicker req={req} lesson={lesson} onResult={setGame} kind={gameKind} setKind={setGameKind} />}
        </div>}
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">One thing I'm grateful for today</p>
        <input value={data.gratitude} onChange={(e) => setData({ ...data, gratitude: e.target.value })} data-testid="gratitude-input"
          className="mt-3 w-full px-5 py-3 rounded-2xl bg-white/70 border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E]" />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-stone-500">How do you feel now?</p>
        <div className="mt-3"><MoodPicker value={data.mood_after} onChange={(v) => setData({ ...data, mood_after: v })} testId="mood-after" /></div>
      </div>
      <button onClick={save} disabled={saving} data-testid="submit-journal-btn"
        className="px-6 py-3 rounded-full bg-[#C87D53] text-white hover:bg-[#B36C44] disabled:opacity-60 transition-colors">
        {saving ? "Saving…" : "Save to journal"}
      </button>
    </div>
  );
};
